// socket/useRealTimeWallSocket.js
import { useCallback, useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';

export const useRealTimeWallSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [wallPosts, setWallPosts] = useState({});
  const [wallLeaderboards, setWallLeaderboards] = useState({});
  const [wallOnlineUsers, setWallOnlineUsers] = useState({});
  const socketRef = useRef(null);

  const connect = useCallback(() => {
    if (socketRef.current?.connected) return;

    const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 
                      (process.env.NODE_ENV === "production" 
                        ? "https://api.pesacore.fr"
                        : "https://localhost:5000");

    const socket = io(`${SOCKET_URL}/realTimeWall`, {
      withCredentials: true,
      transports: process.env.NODE_ENV === 'development' ? ['polling'] : ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[RealTimeWall] Connecté (indépendant)');
      setIsConnected(true);
    });

    socket.on('disconnect', (reason) => {
      console.log('[RealTimeWall] Déconnecté:', reason);
      setIsConnected(false);
    });

    socket.on('connect_error', (err) => {
      console.error('[RealTimeWall] Erreur de connexion:', err.message);
    });

    socket.on('wall:newPost', (data) => {
      const roomId = data.wallConfigId;
      setWallPosts(prev => ({
        ...prev,
        [roomId]: [data, ...(prev[roomId] || [])]
      }));
    });

    socket.on('wall:reaction', ({ postId, reaction, count }) => {
        setWallPosts(prev => {
            const next = { ...prev };
            for (const roomId in next) {
            next[roomId] = next[roomId].map(post =>
                post._id === postId
                ? {
                    ...post,
                    // Conserver Map si c'est déjà une Map, sinon objet plain
                    reactions: post.reactions instanceof Map
                        ? new Map([...post.reactions, [reaction, count]])
                        : { ...post.reactions, [reaction]: count }
                    }
                : post
            );
            }
            return next;
        });
        });

    socket.on('wall:joined', ({ activityId, moduleId }) => {
      console.log(`[RealTimeWall] Rejoint room: activity_${activityId || moduleId}`);
    });

    socket.on('wall:error', ({ message }) => {
      console.error('[RealTimeWall] Erreur serveur:', message);
    });

    // À terme, ajoutez les événements pour leaderboards et online users
    // socket.on('wall:leaderboard', (data) => {...});
    // socket.on('wall:onlineUsers', (data) => {...});

    return socket;
  }, []);

  useEffect(() => {
    const socket = connect();
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [connect]);

  const joinWall = useCallback(({ activityId, moduleId }) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('joinWall', { activityId, moduleId });
    } else {
      console.warn('[RealTimeWall] Socket non connecté pour joinWall');
    }
  }, []);

  const leaveWall = useCallback(({ activityId, moduleId }) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('leaveWall', { activityId, moduleId });
    }
  }, []);

  const sendWallPost = useCallback((data) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('wall:newPost', data);
    } else {
      console.warn('[RealTimeWall] Socket non connecté pour sendWallPost');
    }
  }, []);

  const sendWallReaction = useCallback(({ postId, reaction }) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('wall:reaction', { postId, reaction });
    }
  }, []);

  return {
    isConnected,
    wallPosts,
    wallLeaderboards,
    wallOnlineUsers,
    joinWall,
    leaveWall,
    sendWallPost,
    sendWallReaction,
  };
};